import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { childrenPropTypes } from '../utils/generic-prop-types';
import { nsOptions } from '../i18n';
import api from '../api';
import { queriesActions } from '../redux/actions';
import NewModal from './NewModal';
import { CardLoader } from './Loader';
import LabeledSelect from './LabeledSelect';
import Pagination from './Pagination';
import ErrorUtil from '../utils/ErrorUtil';
import NewTooltip from './NewTooltip';
import {
  QUERY_STATUS_NEW,
  QUERY_STATUS_COMMENTED,
  QUERY_STATUS_EDITED,
  QUERY_STATUS_RESOLVED,
  QUERY_STATUS_CLOSED,
  QUERY_STATUS_COLORS,
  QUERY_COMMENT_REQUIRED_STATUSES,
  QUERY_COMMENT_MAX_LENGTH,
  HISTORY_FILTER_ALL,
  HISTORY_FILTER_QUERIES,
  HISTORY_FILTER_AUDIT,
  DATE_TIME,
} from '../constants';
import Formatter from '../utils/Formatter';

const STATUS_LABEL_KEYS = {
  [QUERY_STATUS_NEW]: 'inclusion:queries.status-new',
  [QUERY_STATUS_COMMENTED]: 'inclusion:queries.status-commented',
  [QUERY_STATUS_EDITED]: 'inclusion:queries.status-edited',
  [QUERY_STATUS_RESOLVED]: 'inclusion:queries.status-resolved',
  [QUERY_STATUS_CLOSED]: 'inclusion:queries.status-closed',
};

const TAB_HISTORY = 'history';
const TAB_UPDATE = 'update';

const STATUS_TOOLTIP_KEYS = {
  [QUERY_STATUS_EDITED]: 'inclusion:queries.status-tooltip-edited',
  [QUERY_STATUS_CLOSED]: 'inclusion:queries.status-tooltip-closed',
  [QUERY_STATUS_COMMENTED]: 'inclusion:queries.status-tooltip-commented',
  [QUERY_STATUS_RESOLVED]: 'inclusion:queries.status-tooltip-resolved',
};

const StatusBadge = ({ status, t }) => {
  const colors = QUERY_STATUS_COLORS[status];
  if (!colors) return null;
  return (
    <span
      style={{
        backgroundColor: colors.bg,
        color: colors.fg,
        padding: '2px 10px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        fontWeight: 600,
        display: 'inline-block',
      }}
    >
      {t(STATUS_LABEL_KEYS[status])}
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  projectUsers: state.projectUsers,
  currentUser: state.auth.authUser,
});

const mapDispatchToProps = (dispatch) => ({
  createQuery: (data) => dispatch(queriesActions.create(data)),
  transitionQuery: (id, toStatus, comment) => (
    dispatch(queriesActions.transition(id, toStatus, comment))
  ),
});

@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
class QueryModal extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    children: childrenPropTypes().isRequired,
    variableName: PropTypes.string.isRequired,
    inclusion: PropTypes.shape().isRequired,
    projectElement: PropTypes.shape().isRequired,
    moduleInstanceId: PropTypes.number,
    queryId: PropTypes.number,
    onSaved: PropTypes.func,
    projectUsers: PropTypes.shape().isRequired,
    createQuery: PropTypes.func.isRequired,
    transitionQuery: PropTypes.func.isRequired,
  };

  static defaultProps = {
    moduleInstanceId: null,
    queryId: null,
    onSaved: () => {},
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      submitting: false,
      query: null,
      selectedStatus: '',
      commentValue: '',
      // Retour #5: default tab is history for existing queries
      activeTab: TAB_HISTORY,
      historyFilter: HISTORY_FILTER_QUERIES,
      historyNewestFirst: true,
      historyPage: 1,
      auditTrailEntries: [],
      error: null,
    };
  }

  isCreateMode = () => !this.props.queryId;

  fetchData = async () => {
    if (this.isCreateMode()) {
      this.setState({
        query: null,
        selectedStatus: '',
        commentValue: '',
        error: null,
        auditTrailEntries: [],
        activeTab: TAB_UPDATE,
      });
      return;
    }
    this.setState({ loading: true, error: null, activeTab: TAB_HISTORY });
    try {
      const query = await api.read('queries', this.props.queryId, {}, {
        cache: { useCache: false },
      });
      const auditTrailEntries = await this.fetchAuditTrailEntries();
      this.setState({
        query,
        selectedStatus: '',
        commentValue: '',
        auditTrailEntries,
      });
    } catch (error) {
      ErrorUtil.handleCatched(this.props, error);
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchAuditTrailEntries = async () => {
    const { inclusion, projectElement, moduleInstanceId } = this.props;
    if (!inclusion || !projectElement) return [];
    try {
      const params = {
        object_model: 'ProjectEntry',
        ordering: '-created_at',
        page_size: 10000,
        details__project: inclusion.project,
        details__inclusion: inclusion.id,
        details__project_element: projectElement.id,
      };
      if (moduleInstanceId) {
        params.details__module = moduleInstanceId;
      }
      const res = await api.requestData('audit-trail', null, 'get', params, undefined);
      return res.results || [];
    } catch (err) {
      return [];
    }
  };

  isInvestigatorOnly = () => {
    const { query } = this.state;
    if (!query) return false;
    return query.allowed_transitions.length === 0
      && query.current_status === QUERY_STATUS_CLOSED;
  };

  isCommentRequired = () => {
    const { selectedStatus } = this.state;
    if (this.isCreateMode()) return true;
    if (!selectedStatus) return false;
    return QUERY_COMMENT_REQUIRED_STATUSES.includes(selectedStatus);
  };

  isUpdateButtonDisabled = () => {
    const { commentValue, submitting } = this.state;
    if (submitting) return true;
    if (this.isCreateMode()) {
      return !commentValue.trim() || commentValue.length > QUERY_COMMENT_MAX_LENGTH;
    }
    if (!this.state.selectedStatus) return true;
    if (this.isCommentRequired() && !commentValue.trim()) return true;
    if (commentValue.length > QUERY_COMMENT_MAX_LENGTH) return true;
    return false;
  };

  handleStatusChange = (event) => {
    this.setState({ selectedStatus: event.target.value });
  };

  handleCommentChange = (event) => {
    this.setState({ commentValue: event.target.value });
  };

  handleHistoryFilterChange = (event) => {
    this.setState({ historyFilter: event.target.value, historyPage: 1 });
  };

  handleHistoryOrderChange = (event) => {
    this.setState({ historyNewestFirst: event.target.value === 'newest', historyPage: 1 });
  };

  handleSubmit = async () => {
    const { commentValue, selectedStatus, query } = this.state;
    const {
      inclusion, projectElement, moduleInstanceId, createQuery, transitionQuery,
      onSaved,
    } = this.props;
    this.setState({ submitting: true, error: null });
    try {
      if (this.isCreateMode()) {
        const created = await createQuery({
          inclusion: inclusion.id,
          project_element: projectElement.id,
          module: moduleInstanceId,
          current_comment: commentValue.trim(),
        });
        await onSaved(created);
      } else {
        const updated = await transitionQuery(query.id, selectedStatus, commentValue.trim());
        await onSaved(updated);
        this.setState({ query: updated, selectedStatus: '', commentValue: '' });
      }
      // Retour #6: close modal after submit
      if (this.modal) {
        this.modal.hide();
      }
    } catch (error) {
      this.setState({ error: error.message || String(error) });
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleCancel = () => {
    if (this.modal) this.modal.hide();
  };

  resolveUserName = (userId, fallbackName) => {
    const { projectUsers } = this.props;
    const projectUser = Object.values(projectUsers).find(
      (pu) => pu.user && pu.user.id === userId,
    );
    if (projectUser && projectUser.user && projectUser.user.label) {
      return projectUser.user.label;
    }
    return fallbackName || '';
  };

  // Retour #5: tab system — "Mettre à jour" tab
  renderUpdateTab = () => {
    const { t } = this.props;
    const {
      query, selectedStatus, commentValue, error,
    } = this.state;
    const createMode = this.isCreateMode();

    if (!createMode && this.isInvestigatorOnly()) {
      return null;
    }

    const statusOrder = [
      QUERY_STATUS_EDITED, QUERY_STATUS_COMMENTED,
      QUERY_STATUS_RESOLVED, QUERY_STATUS_CLOSED,
    ];
    const allowedTransitions = createMode
      // eslint-disable-next-line camelcase
      ? [] : (query?.allowed_transitions || [])
        .slice().sort((a, b) => (
          statusOrder.indexOf(a) - statusOrder.indexOf(b)
        ));
    const commentRequired = this.isCommentRequired();

    return (
      <div className="mt-4">
        {!createMode && (
          <p className="text-muted mb-3">{t('inclusion:queries.complete-to-update')}</p>
        )}

        {/* Retour #9: indented, no dashed underline */}
        <div className="form-group d-flex align-items-baseline mb-3 ml-3">
          <span className="text-muted" style={{ minWidth: '130px' }}>
            {createMode ? t('inclusion:queries.current-status') : t('inclusion:queries.new-status')}
          </span>
          <div className="ml-3">
            {createMode ? (
              <StatusBadge status={QUERY_STATUS_NEW} t={t} />
            ) : (
              <div className="d-flex flex-wrap">
                {allowedTransitions.map((s) => (
                  <div
                    key={s}
                    className="custom-control custom-radio d-inline-block mr-3"
                  >
                    <input
                      type="radio"
                      name="query-status"
                      id={`query-status-${s}`}
                      value={s}
                      checked={selectedStatus === s}
                      onChange={this.handleStatusChange}
                      className="custom-control-input"
                    />
                    <label
                      className="custom-control-label"
                      htmlFor={`query-status-${s}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {STATUS_TOOLTIP_KEYS[s] ? (
                        <NewTooltip content={t(STATUS_TOOLTIP_KEYS[s])}>
                          <span style={{ borderBottom: '1px dotted currentColor' }}>
                            {t(STATUS_LABEL_KEYS[s])}
                          </span>
                        </NewTooltip>
                      ) : (
                        <span>{t(STATUS_LABEL_KEYS[s])}</span>
                      )}
                    </label>
                  </div>
                ))}
                {allowedTransitions.length === 0 && (
                  <span className="text-muted font-italic">
                    {t('inclusion:queries.closed-locked-for-investigator')}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Retour #9 + #11: indented, padding on textarea placeholder */}
        <div className="form-group d-flex align-items-start mb-3 ml-3">
          <div style={{ minWidth: '130px', paddingTop: '0.4rem' }}>
            <span style={{ color: '#6C757D' }}>
              {t('inclusion:queries.comment')}
              {commentRequired && <span className="text-danger ml-1">*</span>}
            </span>
            <br />
            <small className="text-muted">
              {t('inclusion:queries.comment-max-chars', { max: QUERY_COMMENT_MAX_LENGTH })}
            </small>
          </div>
          <div className="ml-3 flex-grow-1">
            <textarea
              className="form-control"
              style={{ border: '1px solid #ced4da', padding: '0.75rem' }}
              value={commentValue}
              onChange={this.handleCommentChange}
              placeholder={t('inclusion:queries.comment-placeholder')}
              rows={4}
              maxLength={QUERY_COMMENT_MAX_LENGTH}
            />
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* Retour #12: Annuler = bold black, Mettre à jour = design system blue */}
        <div className="d-flex justify-content-end mt-3 mb-4">
          <button
            type="button"
            className="btn btn-link btn-cancel-query mr-3"
            onClick={this.handleCancel}
          >
            {t('inclusion:queries.actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-newturquoise-1 text-white px-3"
            onClick={this.handleSubmit}
            disabled={this.isUpdateButtonDisabled()}
          >
            {createMode
              ? t('inclusion:queries.actions.create')
              : t('inclusion:queries.actions.update')}
          </button>
        </div>
      </div>
    );
  };

  // Retour #13: user name + date in bold, comment-dots icon
  renderHistoryEntry = (entry) => {
    const { t } = this.props;
    const formatter = new Formatter(DATE_TIME, undefined);
    const date = formatter.format(moment(entry.created_at));
    const userName = this.resolveUserName(entry.creator, entry.user_name);
    const isCreation = !entry.from_status;
    const statusLabel = t(STATUS_LABEL_KEYS[entry.to_status] || '');

    return (
      <li
        key={`query-history-${entry.id}`}
        className="list-group-item px-0 border-0"
      >
        <div className="d-flex align-items-start">
          <FontAwesomeIcon
            icon={['fas', 'bell']}
            className="text-gray mr-3 mt-1 fa-fw"
          />
          <div>
            <div>
              <b>{userName}</b>
              {' '}
              {isCreation
                ? t('inclusion:queries.audit-created-action')
                : t('inclusion:queries.audit-transition-action', { status: statusLabel })}
              {' '}
              {t('inclusion:queries.audit-date-prefix')}
              {' '}
              <b>{date}</b>
            </div>
            {entry.comment && (
              <div className="ml-4 mt-1 d-flex align-items-start">
                <FontAwesomeIcon
                  icon={['far', 'comment-dots']}
                  className="text-gray mr-2 mt-1 fa-fw"
                />
                <span title={entry.comment}>
                  {t('inclusion:queries.comment-prefix')}
                  {' '}
                  <strong>
                    {entry.comment.length > 80
                      ? `${entry.comment.slice(0, 80)}…`
                      : entry.comment}
                  </strong>
                </span>
              </div>
            )}
          </div>
        </div>
      </li>
    );
  };

  renderAuditTrailEntry = (entry) => {
    const { t } = this.props;
    const formatter = new Formatter(DATE_TIME, undefined);
    const date = formatter.format(moment(entry.created_at));
    const userName = this.resolveUserName(entry.user, entry.user_name);

    // eslint-disable-next-line camelcase
    const { missing } = entry.details || {};
    // eslint-disable-next-line camelcase
    let action = entry.action_type;
    let value = entry.details ? entry.details.value : undefined;
    if (missing) {
      if (action !== 'delete') action = 'create-or-update';
      const missingCode = missing.replace('_', '-');
      action = `${action}-${missingCode}`;
      value = t(`project:audit-trail.${missingCode}`);
    }
    const actionText = t(`project:audit-trail.action.detailed.${action}-variable`,
      { defaultValue: t(`project:audit-trail.action.${action}-variable`) });

    let actionIcon = ['fas', 'pencil'];
    // eslint-disable-next-line camelcase
    if (entry.action_type === 'create') actionIcon = ['fas', 'plus'];
    // eslint-disable-next-line camelcase
    if (entry.action_type === 'delete') actionIcon = ['fas', 'times'];

    return (
      <li
        key={`audit-${entry.id}`}
        className="list-group-item d-flex align-items-center px-0 border-0"
      >
        <span className="text-gray mr-3">
          <FontAwesomeIcon icon={actionIcon} className="fa-fw" />
        </span>
        <span>
          <b>{userName}</b>
          {' '}
          {actionText}
          {value !== undefined && (
            <span className="font-weight-semibold">
              {' '}
              {String(value)}
            </span>
          )}
          {' '}
          {t('inclusion:queries.audit-date-prefix')}
          {' '}
          <b>{date}</b>
        </span>
      </li>
    );
  };

  handleHistoryPageChange = (page) => {
    this.setState({ historyPage: page });
  };

  // Retour #5: history tab content with client-side pagination
  renderHistoryTab = () => {
    const HISTORY_PAGE_SIZE = 5;
    const { t } = this.props;
    const {
      query, historyFilter, historyNewestFirst, auditTrailEntries, historyPage,
    } = this.state;
    if (this.isCreateMode() || !query) return null;

    const showQueries = historyFilter === HISTORY_FILTER_QUERIES
      || historyFilter === HISTORY_FILTER_ALL;
    const showAudit = historyFilter === HISTORY_FILTER_AUDIT
      || historyFilter === HISTORY_FILTER_ALL;

    let allEntries = [];
    if (showQueries) {
      allEntries = allEntries.concat(
        (query.comments || []).map((c) => ({ ...c, entryType: 'query' })),
      );
    }
    if (showAudit) {
      allEntries = allEntries.concat(
        (auditTrailEntries || []).map((a) => ({ ...a, entryType: 'audit' })),
      );
    }
    allEntries.sort((a, b) => {
      const da = new Date(a.created_at);
      const db = new Date(b.created_at);
      return historyNewestFirst ? db - da : da - db;
    });

    const totalCount = allEntries.length;
    const start = (historyPage - 1) * HISTORY_PAGE_SIZE;
    const entries = allEntries.slice(start, start + HISTORY_PAGE_SIZE);

    return (
      <div className="mt-4 mb-4">
        {/* Retour #13: uniform filter order: Tout / Queries / Audit trail */}
        <div className="d-flex align-items-center mb-3">
          <FontAwesomeIcon icon={['fas', 'filter']} className="text-gray mr-2" />
          <LabeledSelect
            className="mb-0 d-inline-block mr-3"
            selectClassName="custom-select audit-trail-ordering-select"
            name="history-filter"
            hideOptionalLabel
            onChange={this.handleHistoryFilterChange}
            value={historyFilter}
          >
            <option value={HISTORY_FILTER_ALL}>
              {t('inclusion:queries.history-filter-all')}
            </option>
            <option value={HISTORY_FILTER_QUERIES}>
              {t('inclusion:queries.history-filter-queries')}
            </option>
            <option value={HISTORY_FILTER_AUDIT}>
              {t('inclusion:queries.history-filter-audit')}
            </option>
          </LabeledSelect>
          <FontAwesomeIcon icon={['fas', 'sort-alt']} className="text-gray mr-2 ml-2" />
          <LabeledSelect
            className="mb-0 d-inline-block"
            selectClassName="custom-select audit-trail-ordering-select"
            name="history-order"
            hideOptionalLabel
            onChange={this.handleHistoryOrderChange}
            value={historyNewestFirst ? 'newest' : 'oldest'}
          >
            <option value="newest">
              {t('inclusion:queries.history-newest-first')}
            </option>
            <option value="oldest">
              {t('inclusion:queries.history-oldest-first')}
            </option>
          </LabeledSelect>
        </div>
        {entries.length > 0 ? (
          <ul className="list-group">
            {entries.map((entry) => (
              entry.entryType === 'query'
                ? this.renderHistoryEntry(entry)
                : this.renderAuditTrailEntry(entry)
            ))}
          </ul>
        ) : (
          <p className="text-muted font-italic">
            {t('project:audit-trail.nothing-to-display')}
          </p>
        )}
        {totalCount > HISTORY_PAGE_SIZE && (
          <nav className="mt-3">
            <Pagination
              page={historyPage}
              count={totalCount}
              pageSize={HISTORY_PAGE_SIZE}
              action={this.handleHistoryPageChange}
            />
          </nav>
        )}
      </div>
    );
  };

  render() {
    const { t, children, variableName } = this.props;
    const { query, loading, activeTab } = this.state;
    const createMode = this.isCreateMode();

    return (
      <NewModal
        trigger={children}
        title={(
          <span>
            <FontAwesomeIcon
              icon={['far', 'bell']}
              className="text-gray mr-3"
              style={{ opacity: 0.7 }}
            />
            {t('inclusion:queries.title', { variable: variableName })}
          </span>
        )}
        size="md"
        type={1}
        ref={(modal) => { this.modal = modal; }}
        onLoad={this.fetchData}
        modalBodyClassName=""
        extraClass="query-modal"
      >
        {/* Status badge + status explanation */}
        {!createMode && query && (
          <div className="mb-3">
            <span className="text-muted mr-2">{t('inclusion:queries.current-status')}</span>
            <StatusBadge status={query.current_status} t={t} />
            {/* Retour #7: phrase explicitant le statut — inline à droite du badge */}
            {[QUERY_STATUS_COMMENTED, QUERY_STATUS_RESOLVED].includes(query.current_status) && (
              <span className="ml-2" style={{ color: '#FFC83D', fontSize: '0.85rem' }}>
                {`- ${t('inclusion:queries.status-explanation-orange')}`}
              </span>
            )}
            {[QUERY_STATUS_NEW, QUERY_STATUS_EDITED].includes(query.current_status) && (
              <span className="ml-2" style={{ color: '#EC5564', fontSize: '0.85rem' }}>
                {`- ${t('inclusion:queries.status-explanation-red')}`}
              </span>
            )}
            {query.current_status === QUERY_STATUS_CLOSED && (
              <span className="ml-2" style={{ color: '#6d9942', fontSize: '0.85rem' }}>
                {`- ${t('inclusion:queries.status-explanation-closed')}`}
              </span>
            )}
          </div>
        )}

        {loading ? <CardLoader /> : (
          <>
            {/* Tab system — hidden for create mode and investigator-only on closed */}
            {!createMode && query && !this.isInvestigatorOnly() && (
              <div className="big-menu d-flex mb-4">
                <a
                  role="button"
                  tabIndex={0}
                  className={`big-menu-item ${activeTab === TAB_HISTORY ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: TAB_HISTORY })}
                  onKeyPress={(e) => { if (e.key === 'Enter') this.setState({ activeTab: TAB_HISTORY }); }}
                >
                  <span className="big-menu-item-text">
                    {t('inclusion:queries.history')}
                  </span>
                </a>
                <div className="big-menu-divider" />
                <a
                  role="button"
                  tabIndex={0}
                  className={`big-menu-item ${activeTab === TAB_UPDATE ? 'active' : ''}`}
                  onClick={() => this.setState({ activeTab: TAB_UPDATE })}
                  onKeyPress={(e) => { if (e.key === 'Enter') this.setState({ activeTab: TAB_UPDATE }); }}
                >
                  <span className="big-menu-item-text">
                    {t('inclusion:queries.actions.update')}
                  </span>
                </a>
              </div>
            )}

            {/* Create mode: show update form directly */}
            {createMode && this.renderUpdateTab()}

            {/* Existing query: show active tab (fix duplication bug) */}
            {!createMode && query && !this.isInvestigatorOnly() && (
              activeTab === TAB_HISTORY
                ? this.renderHistoryTab()
                : this.renderUpdateTab()
            )}

            {/* Investigator-only on closed: show history only (no tabs, no duplication) */}
            {!createMode && query && this.isInvestigatorOnly() && (
              this.renderHistoryTab()
            )}
          </>
        )}
      </NewModal>
    );
  }
}

export default QueryModal;
